import { animate } from "animejs";

export type VoiceCoreState = "idle" | "listening" | "thinking" | "speaking" | "interrupted" | "complete" | "expired" | "muted";

type AnimationHandle = { cancel: () => unknown; revert?: () => unknown };

function seededDrift(index: number, salt: number, range: number) {
  const value = Math.sin((index + 1) * (salt * 12.9898)) * 43758.5453;
  return ((value - Math.floor(value)) * 2 - 1) * range;
}

function numericData(target: unknown, key: "radius" | "angle") {
  return target instanceof HTMLElement ? Number(target.closest<HTMLElement>(".signal-point")?.dataset[key] ?? 0) : 0;
}

function vibrationData(target: unknown) {
  return target instanceof HTMLElement ? Number(target.closest<HTMLElement>(".signal-point")?.dataset.vibration ?? 0) : 0;
}

function safeCancel(animation: AnimationHandle | null | undefined) {
  if (!animation) return;
  try { animation.revert?.(); } catch { /* best effort cleanup */ }
  try { animation.cancel(); } catch { /* best effort cleanup */ }
}

export class VoiceCoreAnimation {
  private readonly field: HTMLElement | null;
  private readonly dots: HTMLElement[];
  private readonly drifts: HTMLElement[];
  private readonly waves: HTMLElement[];
  private readonly vibrations: HTMLElement[];
  private floatAnimation: AnimationHandle | null = null;
  private ambientAnimation: AnimationHandle | null = null;
  private vibrationAnimation: AnimationHandle | null = null;
  private introAnimation: AnimationHandle | null = null;
  private pulseAnimation: AnimationHandle | null = null;
  private stateAnimation: AnimationHandle | null = null;
  private readonly reducedMotionQuery: MediaQueryList | null;
  private readonly reducedMotionListener: ((event: MediaQueryListEvent) => void) | null;
  private lastPulseAt = 0;
  private energy = 0;

  constructor(private readonly core: HTMLElement) {
    this.field = core.querySelector<HTMLElement>(".particle-field");
    this.dots = [...core.querySelectorAll<HTMLElement>(".signal-dot")];
    this.drifts = [...core.querySelectorAll<HTMLElement>(".signal-drift")];
    this.waves = [...core.querySelectorAll<HTMLElement>(".signal-wave")];
    this.vibrations = [...core.querySelectorAll<HTMLElement>('.signal-point[data-vibrates="true"] .signal-vibration')];
    this.reducedMotionQuery = typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
    this.reducedMotionListener = this.reducedMotionQuery ? (event) => {
      if (event.matches) this.stopMotion();
      else this.startMotion(true);
    } : null;
    if (this.reducedMotionQuery && this.reducedMotionListener) this.reducedMotionQuery.addEventListener("change", this.reducedMotionListener);
    if (!this.reducedMotionQuery?.matches) this.startMotion(true);
  }

  private startMotion(withIntro = false) {
    if (!this.dots.length || this.floatAnimation) return;
    const floatingDrifts = this.drifts.filter((_element, index) => index % 12 === 0);
    const breathingWaves = this.waves.filter((_element, index) => index % 10 === 0);
    this.floatAnimation = animate(floatingDrifts, {
      translateX: (_target, index) => seededDrift(index ?? 0, 0.73, 4.8),
      translateY: (_target, index) => seededDrift(index ?? 0, 1.17, 4.2),
      duration: (_target, index) => 4200 + ((index ?? 0) % 11) * 260,
      delay: (_target, index) => ((index ?? 0) % 17) * -170,
      ease: "inOutSine",
      alternate: true,
      loop: true,
    }) as AnimationHandle;
    this.ambientAnimation = animate(breathingWaves, {
      scale: [0.84, 1.16],
      duration: (target: unknown) => 2800 + numericData(target, "radius") * 2400,
      delay: (target, index) => -numericData(target, "radius") * 1200 - ((index ?? 0) % 19) * 45,
      ease: "inOutSine",
      alternate: true,
      loop: true,
    }) as AnimationHandle;
    this.vibrationAnimation = animate(this.vibrations, {
      translateY: (target: unknown) => vibrationData(target),
      duration: (_target, index) => 620 + ((index ?? 0) % 9) * 52,
      delay: (_target, index) => -((index ?? 0) % 13) * 67,
      ease: "inOutSine",
      alternate: true,
      loop: true,
    }) as AnimationHandle;
    if (withIntro) {
      this.introAnimation = animate(this.dots, {
        scale: [0, 1],
        duration: 720,
        delay: (target, index) => numericData(target, "radius") * 420 + ((index ?? 0) % 9) * 7,
        ease: "out(5)",
      }) as AnimationHandle;
    }
  }

  private stopMotion() {
    safeCancel(this.floatAnimation);
    safeCancel(this.ambientAnimation);
    safeCancel(this.vibrationAnimation);
    safeCancel(this.introAnimation);
    safeCancel(this.pulseAnimation);
    safeCancel(this.stateAnimation);
    this.floatAnimation = null;
    this.ambientAnimation = null;
    this.vibrationAnimation = null;
    this.introAnimation = null;
    this.pulseAnimation = null;
    this.stateAnimation = null;
    [...this.drifts, ...this.waves, ...this.vibrations, ...this.dots].forEach((element) => {
      element.style.removeProperty("transform");
      element.style.removeProperty("opacity");
    });
  }

  setState(state: VoiceCoreState) {
    this.core.dataset.voiceState = state;
    this.field?.style.setProperty("--voice-state-energy", state === "thinking" ? "0.72" : state === "speaking" ? "1" : "0.38");
    if (this.reducedMotionQuery?.matches || !this.dots.length) return;
    safeCancel(this.stateAnimation);
    this.stateAnimation = null;
    if (state === "thinking") {
      const thinkingDots = this.dots.filter((_element, index) => index % 8 === 0);
      this.stateAnimation = animate(thinkingDots, {
        opacity: [0.22, 0.96],
        duration: 920,
        delay: (target: unknown) => numericData(target, "angle") * 760 + numericData(target, "radius") * 120,
        ease: "inOutSine",
        alternate: true,
        loop: true,
      }) as AnimationHandle;
    }
    if (state === "interrupted") this.pulse(0.9, true);
    if (state === "complete") this.pulse(1);
  }

  setEnergy(level: number) {
    const target = Math.max(0, Math.min(1, level));
    this.energy += (target - this.energy) * (target > this.energy ? 0.48 : 0.14);
    this.field?.style.setProperty("--voice-energy", this.energy.toFixed(3));
  }

  pulse(strength: number, reverse = false) {
    if (this.reducedMotionQuery?.matches || !this.dots.length) return;
    const now = Date.now();
    if (this.pulseAnimation || now - this.lastPulseAt < 900) return;
    this.lastPulseAt = now;
    safeCancel(this.introAnimation);
    this.introAnimation = null;
    const safeStrength = Math.max(0.2, Math.min(1, strength));
    this.pulseAnimation = animate(this.dots, {
      scale: [0.7, 1.28 + safeStrength * 1.05, 1],
      duration: 820 + safeStrength * 240,
      delay: (target, index) => {
        const radius = numericData(target, "radius");
        const direction = reverse ? 1 - radius : radius;
        return direction * 460 + ((index ?? 0) % 7) * 5;
      },
      ease: "out(4)",
      onComplete: () => { this.pulseAnimation = null; },
    }) as AnimationHandle;
  }

  destroy() {
    if (this.reducedMotionQuery && this.reducedMotionListener) this.reducedMotionQuery.removeEventListener("change", this.reducedMotionListener);
    this.stopMotion();
    this.field?.style.removeProperty("--voice-energy");
    this.field?.style.removeProperty("--voice-state-energy");
  }
}
